import React, { useMemo } from "react";
import { Card } from "@/components/ui/card";
import { AlertTriangle, Repeat } from "lucide-react";
import { daysBetween, groupByWeek, fmtDay, fmtShort, addDays } from "@/lib/vigilanza";

export function GeneratorPreview({ plan, params, counts, updateItems }) {
  const sById = useMemo(() => Object.fromEntries(plan.students.map((s) => [s.id, s])), [plan.students]);
  const weeks = groupByWeek(daysBetween(params.date_from, params.date_to), (d) => d);
  const cell = useMemo(() => {
    const c = {};
    plan.items.forEach((i) => { (c[`${i.mansione_id}|${i.date}`] ||= []).push(i); });
    return c;
  }, [plan.items]);
  const holes = useMemo(() => {
    const c = {};
    plan.unfilled.forEach((u) => { c[`${u.mansione_id}|${u.date}`] = (c[`${u.mansione_id}|${u.date}`] || 0) + 1; });
    return c;
  }, [plan.unfilled]);
  const sorted = [...plan.students].sort((a, b) => (counts[b.id] || 0) - (counts[a.id] || 0) || a.name.localeCompare(b.name));
  const vals = plan.students.map((s) => counts[s.id] || 0);

  // In una cella, una persona ha un turno per ogni fascia oraria: le modifiche valgono per tutte le sue fasce del giorno
  const change = (m, date, oldSid, sid) => updateItems((items) => {
    const hit = (i) => i.mansione_id === m.id && i.date === date && i.student_id === oldSid;
    return sid ? items.map((i) => (hit(i) ? { ...i, student_id: sid, substitute_for: null, substitute_reason: null } : i)) : items.filter((i) => !hit(i));
  });
  const add = (m, date, sid) => sid && updateItems((items) => [...items, ...m.time_slots.map((s, n) => ({ key: `${m.id}-${date}-${Date.now()}-${n}`, mansione_id: m.id, date, student_id: sid, start_time: s.start_time, end_time: s.end_time }))]);
  const canDo = (m, s) => !(m.excluded_student_ids || []).includes(s.id) && (!m.gender || s.gender === m.gender);
  const dayItems = useMemo(() => {
    const c = {};
    plan.items.forEach((i) => { (c[`${i.student_id}|${i.date}`] ||= []).push(i); });
    return c;
  }, [plan.items]);
  // Libero per la mansione m nel giorno d: nessun impegno/permesso/turno sovrapposto alle sue fasce
  const isFree = (m, d, sid) => m.time_slots.every((sl) =>
    !(plan.busy?.[`${sid}|${d}`] || []).some(([s, e]) => overlap(sl.start_time, sl.end_time, s, e)) &&
    !(dayItems[`${sid}|${d}`] || []).some((i) => i.mansione_id !== m.id && overlap(sl.start_time, sl.end_time, i.start_time, i.end_time)));
  const freeOfDay = (d) => plan.students.filter((s) => !(plan.busy?.[`${s.id}|${d}`] || []).length && !(dayItems[`${s.id}|${d}`] || []).length);
  const options = (m, d, current) => {
    const list = plan.students.filter((s) => s.id === current || canDo(m, s));
    const taken = new Set(people(m, d).map((i) => i.student_id));
    return {
      free: list.filter((s) => s.id === current || (!taken.has(s.id) && isFree(m, d, s.id))),
      busy: list.filter((s) => s.id !== current && (taken.has(s.id) || !isFree(m, d, s.id))),
    };
  };
  const people = (m, d) => {
    const seen = new Map();
    (cell[`${m.id}|${d}`] || []).forEach((i) => { if (!seen.has(i.student_id)) seen.set(i.student_id, i); });
    return [...seen.values()];
  };

  return (
    <div className="mt-6 space-y-6" data-testid="gen-preview-result">
      <Card className="p-4 border border-stone-200 shadow-none rounded-xl">
        <div className="flex flex-wrap gap-4 text-sm mb-3">
          <span><b>{plan.items.length}</b> turni proposti</span>
          <span><b>{plan.students.length}</b> studenti in rotazione</span>
          <span>Carico: min <b>{Math.min(...vals)}</b> · max <b>{Math.max(...vals)}</b></span>
          {plan.items.some((i) => i.substitute_for) && <span className="text-amber-700 inline-flex items-center gap-1"><Repeat size={13}/> {plan.items.filter((i) => i.substitute_for).length} sostituzioni per impegni esterni</span>}
          {plan.unfilled.length > 0 && <span className="text-red-700 inline-flex items-center gap-1" data-testid="gen-unfilled"><AlertTriangle size={13}/> {plan.unfilled.length} posti scoperti</span>}
        </div>
        {plan.missing_gender?.length > 0 && (
          <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3" data-testid="gen-missing-gender">
            {plan.missing_gender.length} studenti senza sesso indicato nella scheda non vengono assegnati alle mansioni "solo uomini/donne": {plan.missing_gender.join(", ")}
          </div>
        )}
        <div className="flex flex-wrap gap-1.5" data-testid="gen-load">
          {sorted.map((s) => <span key={s.id} className="text-xs px-2 py-0.5 rounded-full bg-stone-50 border border-stone-200" title="Turni proposti · turni svolti nell'ultimo mese">{s.name} · <b className="font-mono">{counts[s.id] || 0}</b>{s.history ? <span className="text-stone-400 font-mono"> +{s.history}</span> : null}</span>)}
        </div>
        <div className="text-[11px] text-stone-400 mt-2">Il numero grigio indica i turni svolti nell&apos;ultimo mese: il generatore ne tiene conto per equità e rotazione.</div>
      </Card>

      {weeks.map((w) => (
        <div key={w.monday}>
          <div className="text-[11px] font-mono uppercase tracking-wider text-stone-500 mb-1.5">Settimana {fmtShort(w.monday)} – {fmtShort(addDays(w.monday, 6))}</div>
          <Card className="border border-stone-200 shadow-none rounded-xl overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead><tr className="bg-stone-50 text-[11px] uppercase tracking-wider text-stone-500">
                <th className="text-left px-3 py-2 w-44">Mansione</th>
                {w.items.map((d) => <th key={d} className="text-left px-2 py-2">{fmtDay(d)}</th>)}
              </tr></thead>
              <tbody className="divide-y divide-stone-100">
                {plan.mansioni.map((m) => (
                  <tr key={m.id} className="align-top">
                    <td className="px-3 py-2 font-medium"><span className="inline-block w-2.5 h-2.5 rounded-full mr-2" style={{ background: m.color }}/>{m.name}
                      <div className="text-[11px] text-stone-400 font-normal">{m.all_students ? "Tutti" : `${m.resources || 1} pers.`}{m.time_slots.filter((s) => s.start_time).map((s) => ` · ${s.start_time}${s.end_time ? `–${s.end_time}` : ""}`).join("")}</div></td>
                    {w.items.map((d) => (
                      <td key={d} className="px-2 py-2 space-y-1" data-testid={`gen-cell-${m.id}-${d}`}>
                        {m.all_students ? (people(m, d).length > 0 && <span className="inline-block text-xs px-2 py-1 rounded bg-violet-50 text-violet-800 border border-violet-200" title={people(m, d).map((i) => sById[i.student_id]?.name).join(", ")}>Tutti · {people(m, d).length}</span>)
                          : people(m, d).map((i) => (
                          <PersonSelect key={i.key} item={i} sById={sById} opts={options(m, d, i.student_id)} onChange={(sid) => change(m, d, i.student_id, sid)}/>
                        ))}
                        {holes[`${m.id}|${d}`] > 0 && (
                          <select value="" onChange={(e) => add(m, d, e.target.value)} data-testid={`gen-fill-${m.id}-${d}`}
                            className="w-full h-7 rounded border border-red-300 bg-red-50 text-red-800 text-xs px-1">
                            <option value="">Scoperto · assegna…</option>
                            <OptGroups opts={options(m, d)}/>
                          </select>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
                <tr className="align-top bg-emerald-50/40" data-testid={`gen-free-row-${w.monday}`}>
                  <td className="px-3 py-2 font-medium text-emerald-800">Studenti liberi
                    <div className="text-[11px] text-stone-400 font-normal">nessuna mansione né impegno</div></td>
                  {w.items.map((d) => {
                    const free = freeOfDay(d);
                    return (
                      <td key={d} className="px-2 py-2" data-testid={`gen-free-${d}`}>
                        <div className="text-[11px] font-mono text-emerald-800 mb-1">{free.length} liberi</div>
                        <div className="flex flex-wrap gap-1 max-h-28 overflow-y-auto">
                          {free.map((s) => <span key={s.id} className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-emerald-200 text-emerald-900">{s.name}</span>)}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </Card>
        </div>
      ))}
    </div>
  );
}

function overlap(a1, a2, b1, b2) {
  if (!a1 || !b1 || a1 === b1) return true;
  return a1 < (b2 || b1) && b1 < (a2 || a1);
}

function OptGroups({ opts, item, sub }) {
  return (
    <>
      <optgroup label={`Liberi (${opts.free.length - (item ? 1 : 0)})`}>
        {opts.free.map((s) => <option key={s.id} value={s.id}>{sub && s.id === item?.student_id ? "↺ " : ""}{s.name}</option>)}
      </optgroup>
      {opts.busy.length > 0 && (
        <optgroup label={`Occupati (${opts.busy.length})`}>
          {opts.busy.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </optgroup>
      )}
    </>
  );
}

function PersonSelect({ item, sById, opts, onChange }) {
  const sub = !!item.substitute_for;
  const title = sub ? `Sostituisce ${sById[item.substitute_for]?.name || "il titolare"} · ${item.substitute_reason || "impegno"}` : undefined;
  return (
    <select value={item.student_id} onChange={(e) => onChange(e.target.value)} title={title} data-testid={`gen-item-${item.key}`}
      className={`w-full h-7 rounded border text-xs px-1 ${sub ? "border-amber-300 bg-amber-50 text-amber-900" : "border-stone-200 bg-white"}`}>
      <OptGroups opts={opts} item={item} sub={sub}/>
      <option value="">— Rimuovi —</option>
    </select>
  );
}
